import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Portal from 'components/Portal'
import { bool } from 'prop-types'

import * as spacing from 'styles/spacing'
import { media } from 'styles/media'
import { white, borderColor, greyDark } from 'styles/colors'
import { navHeightMobile } from 'styles/global'

import { NativeAuthConsumer } from 'components/NativeAuth'
import Icon from 'components/Icon'
import MobileNavItem from './MobileNavItem'
import Cart from 'components/Nav/Cart'
import { MobileLogo, LogoWrap } from 'components/Nav'
import ProvinceSelector from 'components/Nav/ProvinceSelector'
import Menu from './Menu'

import Hamburger from 'icons/hamburger.svg'
import Close from 'icons/close.svg'

const Toggle = styled.button`
  display: flex;
  padding: ${spacing.small};
  margin: -${spacing.small};

  ${media.medium`
    display: none;
  `};
`

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${white};
  z-index: 800;
  display: ${props => (props.visibility ? 'block' : 'none')};

  ${media.medium`
    display: none;
  `};
`

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`

const Header = styled.header`
  position: relative;
  z-index: 10;
  height: ${navHeightMobile};
  box-shadow: 0 1px 0 0 ${borderColor};
  padding: 0 ${spacing.large};
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Scroll = styled.div`
  flex: 1;
  overflow-y: scroll;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
`

const Section = styled.div`
  padding: ${spacing.large} 0;

  & + & {
    border-top: 1px solid ${borderColor};
  }
`

const CloseIcon = styled.div`
  display: flex;
  color: ${greyDark};
`

const MobileNav = ({ hideLogin }) => {
  const [visibility, setVisibility] = useState(false)

  useEffect(() => {
    document.body.style.overflow = visibility ? 'hidden' : 'visible'
    document.body.style.position = visibility ? 'fixed' : 'static'
    // clean up when unmounted
    return () => {
      document.body.style.overflow = 'visible'
      document.body.style.position = 'static'
    }
  }, [visibility])

  const hideNav = () => {
    setVisibility(false)
  }

  return (
    <>
      <Toggle
        onClick={() => {
          setVisibility(!visibility)
        }}
      >
        <Icon icon={<Hamburger />} />
      </Toggle>
      <Portal selector={'vype-mobile-nav'}>
        <Overlay visibility={visibility}>
          <Inner>
            <Header>
              <Toggle
                onClick={() => {
                  setVisibility(!visibility)
                }}
              >
                <CloseIcon>
                  <Icon icon={<Close />} />
                </CloseIcon>
              </Toggle>
              <LogoWrap to="/" onClick={hideNav}>
                <MobileLogo />
              </LogoWrap>
              <Cart />
            </Header>
            <Scroll>
              <Section>
                <Menu />
              </Section>
              <Section>
                {!hideLogin && (
                  <NativeAuthConsumer>
                    {({ user, logout, setLoginModalVisibility }) => {
                      if (user) {
                        return (
                          <>
                            <MobileNavItem hideNav={hideNav} link="/account/" label="My Account" />
                            <MobileNavItem hideNav={hideNav} link="/account/rewards/" label="My Rewards" />
                            <MobileNavItem hideNav={hideNav} link="/account/savings/" label="My Savings" />
                            <MobileNavItem hideNav={hideNav} link="/account/orders/" label="My Orders" />
                            <MobileNavItem hideNav={hideNav} link="/account/subscriptions/" label="My Subscription" />
                            <MobileNavItem
                              hideNav={hideNav}
                              as="button"
                              onClick={() => {
                                logout()
                                setVisibility(false)
                              }}
                              isBold
                              label="Logout"
                            />
                          </>
                        )
                      }

                      return (
                        <>
                          <MobileNavItem
                            hideNav={hideNav}
                            as="button"
                            link="#"
                            onClick={() => {
                              setLoginModalVisibility(true)
                              setVisibility(false)
                            }}
                            data-tracking="nav--link--button--Login/Register"
                            label="Login/Register"
                          />
                          <ProvinceSelector />
                        </>
                      )
                    }}
                  </NativeAuthConsumer>
                )}

                {/* If hideLogin, show province selector but only if no user present */}
                {hideLogin && (
                  <NativeAuthConsumer>
                    {({ user }) => {
                      if (user) {
                        return null
                      }

                      return <ProvinceSelector />
                    }}
                  </NativeAuthConsumer>
                )}
              </Section>
            </Scroll>
          </Inner>
        </Overlay>
      </Portal>
    </>
  )
}

MobileNav.propTypes = {
  hideLogin: bool
}

export default MobileNav
